/**
 * Mock data for onboarding flow (loyalty accounts + trips).
 * Only used in development when USE_ONBOARDING_MOCK_DATA is true.
 * Set USE_ONBOARDING_MOCK_DATA = true below to test the full flow without real account data.
 */
import type {
  FlightReservation,
  FlightSegment,
  Hotel,
  HotelReservation,
  Itineraries,
  Itinerary,
  LoyaltyAccount,
  LoyaltyAccountsResponse,
} from "@/generated/email_parser.openapi";
import {
  ItineraryGroupingEnum,
  LoyaltyProgramEnum,
} from "@/generated/email_parser.openapi";

const isDev =
  typeof process !== "undefined" && process.env.NODE_ENV === "development";

/** Set to true in development to use mock loyalty + trips data for onboarding testing (match mobile: default false). */
export const USE_ONBOARDING_MOCK_DATA = isDev && false;

// --- Mock loyalty accounts (hotel, airline, card) ---

const MOCK_LOYALTY_ACCOUNTS: LoyaltyAccount[] = [
  {
    id: "mock-marriott-1",
    brandName: "Marriott Bonvoy",
    brandType: "hotel",
    loyaltyProgram: LoyaltyProgramEnum.MarriottBonvoy,
    loyaltyId: "MB*****4321",
    loyaltyPoints: 125000,
    loyaltyStatus: "Platinum Elite",
    cashValue: 875,
    parseInProgress: false,
  },
  {
    id: "mock-united-1",
    brandName: "MileagePlus",
    brandType: "airline",
    loyaltyProgram: LoyaltyProgramEnum.MileagePlus,
    loyaltyId: "UA***7890",
    loyaltyPoints: 85000,
    loyaltyStatus: "Premier 1K",
    cashValue: 680,
    parseInProgress: false,
  },
  {
    id: "mock-hilton-1",
    brandName: "Hilton Honors",
    brandType: "hotel",
    loyaltyProgram: LoyaltyProgramEnum.HiltonHonors,
    loyaltyId: "HH*****5555",
    loyaltyPoints: 42000,
    loyaltyStatus: "Diamond",
    cashValue: 252,
    parseInProgress: false,
  },
  {
    id: "mock-amex-1",
    brandName: "The Platinum Card",
    brandType: "card",
    loyaltyProgram: LoyaltyProgramEnum.ThePlatinumCard,
    loyaltyId: "****1234",
    loyaltyPoints: 150000,
    loyaltyStatus: "Member",
    cashValue: 900,
    parseInProgress: false,
  },
  {
    id: "mock-ihg-1",
    brandName: "IHG One Rewards",
    brandType: "hotel",
    loyaltyProgram: LoyaltyProgramEnum.IhgOneRewards,
    loyaltyId: "IHG****6789",
    loyaltyPoints: 78000,
    loyaltyStatus: "Platinum Elite",
    cashValue: 468,
    parseInProgress: false,
  },
  {
    id: "mock-delta-1",
    brandName: "SkyMiles",
    brandType: "airline",
    loyaltyProgram: LoyaltyProgramEnum.SkyMiles,
    loyaltyId: "DL***2468",
    loyaltyPoints: 95000,
    loyaltyStatus: "Diamond Medallion",
    cashValue: 665,
    parseInProgress: false,
  },
  {
    id: "mock-chase-1",
    brandName: "Chase Sapphire Preferred",
    brandType: "card",
    loyaltyProgram: LoyaltyProgramEnum.ChaseSapphirePreferred,
    loyaltyId: "****5678",
    loyaltyPoints: 82000,
    loyaltyStatus: "Member",
    cashValue: 1025,
    parseInProgress: false,
  },
];

/**
 * When USE_ONBOARDING_MOCK_ASYNC_SYNC is true, each account flips parseInProgress→false at this many ms
 * from flow start. Intentionally irregular and batched to test: single syncs, multiple at once, and gaps.
 */
const MOCK_SYNC_SCHEDULE_MS: Record<string, number> = {
  "mock-marriott-1": 5000, // 5s – single
  "mock-hilton-1": 8000, // 8s – single
  "mock-amex-1": 20000, // 20s – batch of three
  "mock-united-1": 20000,
  "mock-delta-1": 20000,
  "mock-ihg-1": 42000, // 42s – batch of two
  "mock-chase-1": 42000,
};

/** Set to true to simulate async parsing: accounts flip parseInProgress at scheduled times. */
export const USE_ONBOARDING_MOCK_ASYNC_SYNC = isDev && true;

let mockFlowStartTimeMs: number | null = null;

/** Reset the mock sync clock so the next fetch starts the schedule from "now". Call when entering pulling screen. */
export function resetOnboardingMockSyncSchedule(): void {
  mockFlowStartTimeMs = null;
}

function getElapsedMs(): number {
  if (mockFlowStartTimeMs === null) mockFlowStartTimeMs = Date.now();
  return Date.now() - mockFlowStartTimeMs;
}

/** For debugging: returns current elapsed ms since mock clock started. */
export function getMockElapsedMs(): number {
  return getElapsedMs();
}

export function getMockLoyaltyAccounts(): LoyaltyAccount[] {
  if (!USE_ONBOARDING_MOCK_ASYNC_SYNC) {
    return MOCK_LOYALTY_ACCOUNTS;
  }
  const elapsed = getElapsedMs();
  return MOCK_LOYALTY_ACCOUNTS.map((acc) => {
    const syncedAtMs = MOCK_SYNC_SCHEDULE_MS[acc.id ?? ""] ?? 0;
    const parseInProgress = elapsed < syncedAtMs;
    return { ...acc, parseInProgress };
  });
}

const MOCK_TOTAL_CASH_VALUE = MOCK_LOYALTY_ACCOUNTS.reduce(
  (sum, a) => sum + (a.cashValue ?? 0),
  0
);

export function getMockTotalCashValue(): number {
  if (!USE_ONBOARDING_MOCK_ASYNC_SYNC) {
    return MOCK_TOTAL_CASH_VALUE;
  }
  const elapsed = getElapsedMs();
  return MOCK_LOYALTY_ACCOUNTS.reduce((sum, acc) => {
    const syncedAtMs = MOCK_SYNC_SCHEDULE_MS[acc.id ?? ""] ?? 0;
    if (elapsed >= syncedAtMs) return sum + (acc.cashValue ?? 0);
    return sum;
  }, 0);
}

/** Returns a LoyaltyAccountsResponse shape for the store and polling shouldStop. */
export function getMockLoyaltyAccountsResponse(): LoyaltyAccountsResponse {
  const items = getMockLoyaltyAccounts();
  const total = getMockTotalCashValue();
  return {
    items,
    accountTotal: items.length,
    totalCashValue: { value: total, currency: "USD" },
    pointsUpdateTotal: undefined,
  };
}

// --- Mock trips (future hotel + flight) ---

const futureDate = new Date();
futureDate.setDate(futureDate.getDate() + 14);
const checkOutDate = new Date(futureDate.getTime() + 3 * 24 * 60 * 60 * 1000);
const checkinStr = futureDate.toISOString().slice(0, 10);
const checkoutStr = checkOutDate.toISOString().slice(0, 10);
const cancellableUntil = new Date(futureDate.getTime() - 24 * 60 * 60 * 1000);

const MOCK_HOTEL_IMAGE_URI =
  "https://images.unsplash.com/photo-1566073771259-6a8506099945?w=800";

const mockHotelReservationSave: HotelReservation = {
  summary: {
    propertyName: "Marriott Downtown",
    cityName: "New York",
    heroImage: MOCK_HOTEL_IMAGE_URI,
  },
  reservation: {
    id: "mock-hotel-save-1",
    name: "Marriott Downtown",
    checkInDatetime: futureDate,
    checkOutDatetime: checkOutDate,
    checkinStr,
    checkoutStr,
    confirmationNumber: "SAVE-12345",
    guestFirstName: "Jane",
    guestLastName: "Doe",
    roomType: "King Room",
    totalCost: 500,
    currency: "USD",
    currentPrice: { value: 450, currency: "USD" },
    refundable: true,
    cancelled: false,
    bookingAgent: "other",
    loyaltyProgram: "Marriott Bonvoy",
    cancellableUntil,
    currentPriceGondolaRateId: 12345,
  } as Hotel,
};

const mockHotelReservationBoost: HotelReservation = {
  summary: {
    propertyName: "Hilton Midtown",
    cityName: "New York",
    heroImage: MOCK_HOTEL_IMAGE_URI,
  },
  reservation: {
    id: "mock-hotel-boost-1",
    name: "Hilton Midtown",
    checkInDatetime: futureDate,
    checkOutDatetime: checkOutDate,
    checkinStr,
    checkoutStr,
    confirmationNumber: "BOOST-67890",
    guestFirstName: "Jane",
    guestLastName: "Doe",
    roomType: "Deluxe King",
    totalCost: 400,
    currency: "USD",
    currentPrice: { value: 400, currency: "USD" },
    refundable: true,
    cancelled: false,
    bookingAgent: "expedia",
    loyaltyProgram: "Hilton Honors",
    cancellableUntil,
    currentPriceGondolaRateId: 67890,
    potentialGondolaCash: { value: 45, currency: "USD" },
  } as Hotel,
};

const mockFlightSegment: FlightSegment = {
  departureIata: "SFO",
  departureDatetime: new Date(futureDate.getTime() + 2 * 60 * 60 * 1000)
    .toISOString()
    .replace(/Z$/, ""),
  arrivalIata: "JFK",
  arrivalDatetime: new Date(futureDate.getTime() + 5 * 60 * 60 * 1000)
    .toISOString()
    .replace(/Z$/, ""),
  flightNumber: "UA 100",
  marketingOperatingCarrier: "United",
};

const mockFlightReservation: FlightReservation = {
  id: "mock-flight-1",
  flightSegments: [mockFlightSegment],
};

const mockItineraryWithHotelSave: Itinerary = {
  id: "mock-itinerary-hotel-save",
  grouping: ItineraryGroupingEnum.Future,
  sortDatetime: futureDate,
  hotels: { items: [mockHotelReservationSave] },
};

const mockItineraryWithHotelBoost: Itinerary = {
  id: "mock-itinerary-hotel-boost",
  grouping: ItineraryGroupingEnum.Future,
  sortDatetime: futureDate,
  hotels: { items: [mockHotelReservationBoost] },
};

const mockItineraryWithFlight: Itinerary = {
  id: "mock-itinerary-flight",
  grouping: ItineraryGroupingEnum.Future,
  sortDatetime: futureDate,
  flights: { items: [mockFlightReservation] },
};

const MOCK_ITINERARIES: Itineraries = {
  items: [
    mockItineraryWithHotelSave,
    mockItineraryWithHotelBoost,
    mockItineraryWithFlight,
  ],
  // No syncStatus = sync complete (trips "complete enough") – match mobile
};

export function getMockItineraries(): Itineraries {
  return MOCK_ITINERARIES;
}
